import React from 'react';
import { siteConfig } from '../../../config/site';

const WhyChooseUs = () => {
  // Climatização: argumentos do próprio serviço em vez dos genéricos de canalização
  const reasons = siteConfig.domain === 'alphaclimatizacao.pt' ? [
    {
      icon: 'ri-medal-line',
      title: 'Técnicos Certificados',
      description: 'Instalação e manutenção feitas por técnicos certificados'
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Visita Técnica Gratuita',
      description: 'Avaliamos o espaço e dizemos-lhe a potência certa antes de comprar'
    },
    {
      icon: 'ri-money-euro-circle-line',
      title: 'Orçamento Gratuito',
      description: 'Avaliação e orçamento sem qualquer custo ou compromisso da sua parte'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Serviço com Garantia',
      description: 'Todos os nossos serviços incluem garantia de qualidade'
    },
    {
      icon: 'ri-stack-line',
      title: 'Um Único Interlocutor',
      description: 'Da pré-instalação em obra à manutenção anual e peças de reposição'
    },
    {
      icon: 'ri-timer-line',
      title: 'Resposta Rápida',
      description: 'Contactamos em menos de 2 horas após receber o seu pedido'
    }
  ] : [
    {
      icon: 'ri-map-pin-user-line',
      title: 'Técnicos Locais',
      description: 'Técnicos especializados na sua zona para um atendimento mais rápido e personalizado'
    },
    {
      icon: 'ri-24-hours-line',
      title: 'Atendimento 24h',
      description: 'Disponíveis todos os dias para emergências e agendamentos de serviços'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Orçamento Gratuito',
      description: 'Avaliação e orçamento sem qualquer custo ou compromisso da sua parte'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Serviço com Garantia',
      description: 'Todos os nossos serviços incluem garantia de qualidade e satisfação'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Preços Transparentes',
      description: 'Sem custos ocultos. O preço acordado é o preço final que paga'
    },
    {
      icon: 'ri-timer-line',
      title: 'Resposta Rápida',
      description: 'Contactamos em menos de 2 horas após receber o seu pedido'
    }
  ];

  const [current, setCurrent] = React.useState(0);
  const [isDesktop, setIsDesktop] = React.useState(false);
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // Build slides responsivamente: 1 item por slide no mobile, 2 no desktop
  const slides = React.useMemo(() => {
    if (!isDesktop) return reasons.map((r) => [r]);
    const chunked: typeof reasons[][] = [] as any;
    for (let i = 0; i < reasons.length; i += 2) {
      chunked.push(reasons.slice(i, i + 2));
    }
    return chunked;
  }, [isDesktop, reasons]);

  const next = () => setCurrent((prev) => (prev + 1) % slides.length);
  const prev = () => setCurrent((prev) => (prev - 1 + slides.length) % slides.length);

  // Autoplay
  React.useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => window.clearInterval(id);
  }, [slides.length, paused]);

  return (
    <section id="sobre" className="py-4 bg-gradient-to-br from-blue-50 to-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-2 bg-gradient-to-b from-transparent to-blue-100/60 rounded-t-2xl mb-2" />
        {/* Título compacto */}
        <div className="text-center mb-4">
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-blue-900">
            Porquê Escolher a {siteConfig.name}
          </h2>
        </div>

        {/* Slider */}
        <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          {/* Track */}
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {slides.map((group, slideIdx) => (
                <div key={slideIdx} className="min-w-full px-2">
                  <div className={`grid ${isDesktop ? 'lg:grid-cols-2' : ''} gap-6 lg:gap-8 max-w-5xl mx-auto`}>
                    {group.map((reason, index) => (
                      <div
                        key={index}
                        className={`bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-300 ${
                          index % 2 === 0 && isDesktop ? 'lg:flex lg:flex-row-reverse lg:items-center' : ''
                        }`}
                      >
                        <div className={`flex ${index % 2 === 0 && isDesktop ? 'lg:justify-end' : 'lg:justify-start'} justify-center mb-6 lg:mb-0`}>
                          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center shadow-sm">
                            <i className={`${reason.icon} text-2xl text-blue-700`}></i>
                          </div>
                        </div>
                        <div className="text-center lg:text-left lg:px-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
                            {reason.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed text-sm">
                            {reason.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Arrows e dots escondidos para manter como faixa compacta */}
        </div>

        <div className="mt-2 h-2 bg-gradient-to-t from-transparent to-blue-100/60 rounded-b-2xl" />
      </div>
    </section>
  );
};

export default WhyChooseUs;
